import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
} from "firebase/auth";
import { auth } from "../../config";
import apiClient from "../../utils/apiClient";
import { useAuthStatus } from "../../hooks/useAuthState";
import Loader from "../../components/Loader";
import {
  PRODUCT_BADGE,
  HERO_TITLE,
  HERO_TEXT,
  HERO_FEATURES,
  AUTH_MOBILE_INTRO,
  SIGNUP_SUBTITLE,
  SIGNIN_SUBTITLE,
} from "../../constants/brand";
import "./Home.css";

const Home = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [signup, setSignUp] = useState(true);
  const { loggedIn } = useAuthStatus();
  const navigate = useNavigate();

  useEffect(() => {
    if (loggedIn) navigate("/dashboard");
  }, [loggedIn, navigate]);

  const switchMode = (isSignup) => {
    setSignUp(isSignup);
    setError("");
  };

  const SignUp = async (e) => {
    e.preventDefault();
    setProcessing(true);
    setError("");

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await sendEmailVerification(userCredential.user);

      const { data } = await apiClient.post("setup-stripe-account", {
        firstName,
        lastName,
        email,
      });

      if (data.error) {
        setError(data.error);
        setProcessing(false);
        return;
      }

      navigate("/dashboard");
    } catch (err) {
      console.error(err);
      const message =
        err.response?.data?.error ||
        (err.code === "auth/email-already-in-use"
          ? "Email is already in use."
          : err.message || "Could not create your account. Please try again.");
      setError(message);
      setProcessing(false);
    }
  };

  const signIn = async (e) => {
    e.preventDefault();
    setProcessing(true);
    setError("");

    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate("/dashboard");
    } catch (err) {
      console.log(err);
      setError("Invalid email or password.");
      setProcessing(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-brand">
        <div className="auth-brand__content">
          <span className="auth-brand__badge">{PRODUCT_BADGE}</span>
          <h1 className="auth-brand__title">{HERO_TITLE}</h1>
          <p className="auth-brand__text">{HERO_TEXT}</p>
          <ul className="auth-brand__features">
            {HERO_FEATURES.map((feature) => (
              <li key={feature}>{feature}</li>
            ))}
          </ul>
        </div>
      </div>

      <div className="auth-panel">
        <div className="auth-mobile-intro">
          <span className="auth-brand__badge">{PRODUCT_BADGE}</span>
          <p className="auth-mobile-intro__text">{AUTH_MOBILE_INTRO}</p>
        </div>
        <div className="auth-card">
          <div className="auth-tabs">
            <button
              type="button"
              className={`auth-tab ${signup ? "auth-tab--active" : ""}`}
              onClick={() => switchMode(true)}
            >
              Sign up
            </button>
            <button
              type="button"
              className={`auth-tab ${!signup ? "auth-tab--active" : ""}`}
              onClick={() => switchMode(false)}
            >
              Sign in
            </button>
          </div>

          <form onSubmit={signup ? SignUp : signIn} className="auth-form">
            <h2 className="auth-form__title">
              {signup ? "Create your account" : "Welcome back"}
            </h2>
            <p className="auth-form__subtitle">
              {signup ? SIGNUP_SUBTITLE : SIGNIN_SUBTITLE}
            </p>

            {signup && (
              <div className="auth-form__row">
                <div className="auth-form__field">
                  <label htmlFor="firstName">First name</label>
                  <input
                    id="firstName"
                    type="text"
                    placeholder="Jane"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                  />
                </div>
                <div className="auth-form__field">
                  <label htmlFor="lastName">Last name</label>
                  <input
                    id="lastName"
                    type="text"
                    placeholder="Doe"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                  />
                </div>
              </div>
            )}

            <div className="auth-form__field">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="auth-form__field">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            {!signup && (
              <div className="auth-form__links">
                <Link to="/forgetPassword">Forgot password?</Link>
              </div>
            )}

            {error && (
              <p className="auth-form__error" role="alert">
                {error}
              </p>
            )}

            <button type="submit" className="auth-form__submit" disabled={processing}>
              {processing ? (
                <Loader size={22} />
              ) : signup ? (
                "Create account"
              ) : (
                "Sign in"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Home;
